import React, { useState, useEffect, useRef } from "react";

const MultiSelectFilter = ({ label = "Label", options = [], selected = [], onChange }) => {
    const [open, setOpen] = useState(false);
    // Max height for the open panel, computed to fill the space between the
    // trigger and the bottom of the viewport so the option list is as long as
    // the remaining vertical space allows.
    const [panelMaxHeight, setPanelMaxHeight] = useState(undefined);
    const containerRef = useRef(null);

    const getIds = (arr) => arr.map((item) => item.id);
    const selectedIds = getIds(selected);

    // Display options alphabetically by name without mutating the source array.
    const sortedOptions = [...options].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );

    const isSelected = (option) => selectedIds.includes(option.id);

    // Edits apply to the parent immediately (no pending/draft); the chart itself
    // only repaints when the refresh button is clicked.
    const toggleOption = (option) => {
        const alreadySelected = selectedIds.includes(option.id);
        onChange(
            alreadySelected
                ? selected.filter((item) => item.id !== option.id)
                : [...selected, option]
        );
    };

    const selectNone = () => onChange([]);

    // While open, close the panel when the user clicks anywhere outside the control.
    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    // While open, size the panel to the vertical space left below the trigger.
    useEffect(() => {
        if (!open) return undefined;
        const updateHeight = () => {
            if (!containerRef.current) return;
            const { bottom } = containerRef.current.getBoundingClientRect();
            const available = window.innerHeight - bottom - 16;
            setPanelMaxHeight(Math.max(available, 160));
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, [open]);

    return (
        <div ref={containerRef} className="relative inline-block w-56 text-left">
            <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className="inline-flex w-full justify-between rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
            >
                <span className="truncate">
                    {selected.map((s) => s.name).join(", ") || "Select options"}
                </span>
                <svg className="ml-2 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.586l3.71-4.356a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            {open && (
                <div
                    style={{ maxHeight: panelMaxHeight }}
                    className="absolute z-10 mt-2 flex w-full origin-top-right flex-col rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
                >
                    {/* Clear convenience button - deselect everything. */}
                    <div className="border-b border-gray-200 p-2">
                        <button
                            type="button"
                            onClick={selectNone}
                            className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100"
                        >
                            Clear
                        </button>
                    </div>

                    <div className="flex-1 overflow-auto py-1">
                        {/* Actual Options */}
                        {sortedOptions.map((option) => (
                            <button
                                type="button"
                                key={option.id}
                                onClick={() => toggleOption(option)}
                                className={`flex w-full items-center px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                                    isSelected(option)
                                        ? "bg-gray-100 text-gray-900"
                                        : "text-gray-700"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected(option)}
                                    readOnly
                                    className="mr-2"
                                />
                                {option.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectFilter;
